import { useEffect } from "react";
import styled from "styled-components";
import { motion, useAnimation } from "framer-motion";
import { Section, SectionHeading, SectionSubtitle } from "./Common";
import useInView from "../hooks/useInView";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 24px;
  display: grid;
  gap: 14px;
  text-align: left;
  box-shadow: 0 16px 30px rgba(14, 22, 36, 0.08);
`;

const Badge = styled.span`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.accentContrast};
  font-weight: 700;
`;

const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h3};
`;

const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.muted};
  line-height: 1.6;
`;

const steps = [
  {
    title: "Install the extension",
    text: "Load Texmu from the Chrome Web Store, Edge Add-ons, or your browser of choice."
  },
  {
    title: "Build your filters",
    text: "Add trigger words, group topics, and tweak blur intensity from the settings page."
  },
  {
    title: "Surf enjoyably",
    text: "Texmu automatically blurs matching posts, comments, videos, and search results."
  },
  {
    title: "Reveal on demand",
    text: "Click to reveal everything inside a container only when you're ready."
  }
];

function HowItWorks() {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.2, once: true });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <Section id="how" ref={ref}>
      <SectionHeading>How Texmu works</SectionHeading>
      <SectionSubtitle>A focused workflow that helps you stay informed without the stress.</SectionSubtitle>
      <Grid>
        {steps.map((step, index) => (
          <Card
            key={step.title}
            initial="hidden"
            animate={controls}
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: { opacity: 1, y: 0 }
            }}
            transition={{ duration: 0.6, delay: 0.12 + index * 0.12, ease: "easeOut" }}
          >
            <Badge>{index + 1}</Badge>
            <Title>{step.title}</Title>
            <Description>{step.text}</Description>
          </Card>
        ))}
      </Grid>
    </Section>
  );
}

export default HowItWorks;
